import { useCallback, useEffect, useState } from 'react';
import { AppRoot } from '@telegram-apps/telegram-ui';
import { StatsSection } from './components/StatsSection/StatsSection';
import { GameTable } from './components/GameTable/GameTable';
import { AgentList } from './components/AgentList/AgentList';
import { AgentListPage } from './components/AgentListPage/AgentListPage';
import { GameListPage } from './components/GameListPage/GameListPage';
import { ProfilePage } from './components/ProfilePage/ProfilePage';
import { TabBar, TabKey } from './components/TabBar/TabBar';
import { getToken, loginWithPassword, loginWithTelegram, logout } from './helpers/api';
import { initTelegramWebApp, isInTelegram } from './helpers/telegram';
import './App.css';

type LoginState = 'pending' | 'success' | 'failed';

export const App = () => {
  const [loginState, setLoginState] = useState<LoginState>('pending');
  const [loginError, setLoginError] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  const [needPassword, setNeedPassword] = useState(() => !isInTelegram() && !getToken());
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [tab, setTab] = useState<TabKey>('home');

  const login = useCallback(async () => {
    if (!isInTelegram()) {
      setLoginError('');
      if (getToken()) {
        setNeedPassword(false);
        setLoginState('success');
        return;
      }
      setLoginState('failed');
      setNeedPassword(true);
      return;
    }
    setLoginState('pending');
    setLoginError('');
    try {
      await loginWithTelegram();
      setLoginState('success');
    } catch (e) {
      setLoginError(e instanceof Error ? e.message : '登录失败');
      setLoginState('failed');
    }
  }, []);

  const handlePasswordLogin = useCallback(async () => {
    if (!username || !password || submitting) {
      return;
    }
    setSubmitting(true);
    setLoginError('');
    try {
      await loginWithPassword(username, password);
      setPassword('');
      setNeedPassword(false);
      setLoginState('success');
    } catch (e) {
      setLoginError(e instanceof Error ? e.message : '登录失败');
    } finally {
      setSubmitting(false);
    }
  }, [username, password, submitting]);

  const handleLogout = useCallback(async () => {
    if (loggingOut) {
      return;
    }
    setLoggingOut(true);
    await logout();
    setLoggingOut(false);
    setConfirmLogout(false);
    setLoginError('');
    setUsername('');
    setPassword('');
    setNeedPassword(!isInTelegram());
    setLoginState('failed');
  }, [loggingOut]);

  useEffect(() => {
    initTelegramWebApp();
    login();
  }, [login]);

  if (loginState === 'success') {
    return (
      <AppRoot>
        <div className="dashboard">
          {tab === 'home' && (
            <div className="home-stack">
              <StatsSection />
              <AgentList />
              <GameTable />
            </div>
          )}
          {tab === 'agents' && <AgentListPage />}
          {tab === 'games' && <GameListPage />}
          {tab === 'profile' && <ProfilePage onLogout={() => setConfirmLogout(true)} />}
        </div>
        <TabBar active={tab} onChange={setTab} />
        {confirmLogout && (
          <div
            className="sheet-overlay"
            onClick={() => !loggingOut && setConfirmLogout(false)}
          >
            <div className="sheet" onClick={(e) => e.stopPropagation()}>
              <div className="sheet-banner sheet-banner-danger">
                <div className="sheet-banner-title">退出登录</div>
                <div className="sheet-banner-sub">退出后需重新登录才能继续使用</div>
                <div className="sheet-banner-bars" aria-hidden="true">
                  <i /><i /><i />
                </div>
              </div>
              <div className="sheet-body">
                <div className="sheet-text">确定要退出登录吗？</div>
                <div className="sheet-desc">
                  退出后当前登录状态将被清除，代理管理、充值等功能将无法使用，需重新登录。
                </div>
              </div>
              <div className="sheet-actions">
                <button
                  type="button"
                  className="sheet-btn sheet-cancel"
                  disabled={loggingOut}
                  onClick={() => setConfirmLogout(false)}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="sheet-btn sheet-danger"
                  disabled={loggingOut}
                  onClick={handleLogout}
                >
                  {loggingOut ? '退出中...' : '确认退出'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AppRoot>
    );
  }

  if (loginState === 'pending') {
    return (
      <AppRoot>
        <div className="login-gate">
          <span className="spinner" />
          <div className="login-gate-text">正在登录...</div>
        </div>
      </AppRoot>
    );
  }

  if (needPassword) {
    return (
      <AppRoot>
        <div className="login-gate">
          <div className="login-brand">
            <div className="login-logo">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 8h10a5 5 0 0 1 4.6 7.1l-1.1 2.4a2 2 0 0 1-3.5.2l-1.7-2.4H8.7L7 17.7a2 2 0 0 1-3.5-.2l-1.1-2.4A5 5 0 0 1 7 8z" />
                <path d="M8 12v4M6 14h4" />
                <circle cx="16" cy="13" r="1" />
                <circle cx="18.5" cy="15" r="1" />
              </svg>
            </div>
            <div className="login-brand-name">Steam 管理助手</div>
            <div className="login-brand-sub">账号登录，畅享代理管理与充值服务</div>
          </div>
          <div className="login-card">
            <form
              className="login-form"
              onSubmit={(e) => {
                e.preventDefault();
                handlePasswordLogin();
              }}
            >
              <label className="login-field">
                <span className="login-field-label">用户名</span>
                <input
                  className="login-input"
                  type="text"
                  placeholder="请输入用户名"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </label>
              <label className="login-field">
                <span className="login-field-label">密码</span>
                <input
                  className="login-input"
                  type="password"
                  placeholder="请输入密码"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              {loginError && <div className="login-gate-error">{loginError}</div>}
              <button
                type="submit"
                className="login-gate-btn"
                disabled={submitting || !username || !password}
              >
                {submitting ? '登录中...' : '登录'}
              </button>
            </form>
          </div>
        </div>
      </AppRoot>
    );
  }

  return (
    <AppRoot>
      <div className="login-gate">
        <div className="login-brand">
          <div className="login-logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 8h10a5 5 0 0 1 4.6 7.1l-1.1 2.4a2 2 0 0 1-3.5.2l-1.7-2.4H8.7L7 17.7a2 2 0 0 1-3.5-.2l-1.1-2.4A5 5 0 0 1 7 8z" />
              <path d="M8 12v4M6 14h4" />
              <circle cx="16" cy="13" r="1" />
              <circle cx="18.5" cy="15" r="1" />
            </svg>
          </div>
          <div className="login-brand-name">Steam 管理助手</div>
        </div>
        <div className="login-card login-card-sm">
          <div className="login-gate-title">{loginError ? '登录失败' : '已退出登录'}</div>
          {loginError && <div className="login-gate-error">{loginError}</div>}
          <button type="button" className="login-gate-btn" onClick={login}>
            {loginError ? '重试' : '登录'}
          </button>
        </div>
      </div>
    </AppRoot>
  );
};
